import React, { useEffect, useState, NamedExoticComponent, } from 'react';
import { MDXRemote } from 'next-mdx-remote';
import { serialize } from 'next-mdx-remote/serialize';
import { Meta } from '../../Meta';
import { LOGGED_IN_APP_BASE_URL } from '../../../utils/url';
import Image from 'next/image';
import { MarketingPage } from '../../marketing/MarketingPage';
import { CaseStudyData } from '../utils';
import Section from '../../marketing/Section';
import Text from '@/components/marketing/Text';
import { CTABox } from '../../marketing/CTABox';
import CustomerQuote from './CustomerQuote';
import CaseStudyHighlight from './CaseStudyHighlight';
import CaseStudyHeader from './CaseStudyHeader';

interface Props {
  caseStudyData: CaseStudyData;
}

export const CustomerCaseStudy = ({ caseStudyData }: Props) => {
  const [mdxSource, setMdxSource] = useState(null);

  const Img = ({ src, alt, width = '100%' }: JSX.IntrinsicElements['img']) => {
    if (!src || !alt) throw new Error('Image must have `src` and `alt`');
  
    return (
      <Image
        src={src}
        alt={alt}
        width={1200}
        height={1200}
        style={{ width }}
        className="block h-auto my-6 max-md:!w-full rounded-xl"
      />
    );
  };
  
  Img.displayName = 'CaseStudyImg';
  
  const isImageNode = (child: React.ReactNode) => {
    return (
      child &&
      typeof child === 'object' &&
      'type' in child &&
      typeof child.type !== 'string' &&
      (child.type as NamedExoticComponent).displayName === 'CaseStudyImg'
    );
  };

  const Table = ({ children }) => (
    <div className="overflow-x-auto">
      <table className="min-w-full border-collapse border border-plum-200 text-fog-900 table-auto">{children}</table>
    </div>
  );

  const TableRow = ({ children }) => (
    <tr className="even:bg-plum-100">{children}</tr>
  );

  const TableHeader = ({ children }) => (
    <th className="p-3 bg-plum-100 text-fog-900">{children}</th>
  );

  const TableCell = ({ children }) => (
    <td className="p-3 border border-plum-200 text-fog-900">{children}</td>
  );

  // Adjusts browser default scroll to headings to account for sticky nav.
  const HEADING_PADDING_CLASS = 'pt-20 -mt-20 md:pt-24 md:-mt-24';

  const CaseStudyContentWrapper = ({ children }) => (
    <div className='w-full max-w-[1100px] border-x border-silvermist-100'>
      <div className='w-full max-w-[1000px] mx-auto pb-20 border-x border-dashed border-silvermist-100'>
        {children}
      </div>
    </div>
  )

  const components = {
    Section: (props) => <Section {...props} />,
    CustomerQuote: (props) => <CustomerQuote {...props} />,
    CaseStudyHighlight: (props) => <CaseStudyHighlight {...props} />,
    CaseStudyHeader: (props) => <CaseStudyHeader {...props} />,
    CaseStudyContentWrapper: (props) => <CaseStudyContentWrapper {...props} />,
    a: ({ href, children }: JSX.IntrinsicElements['a']) => (
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className="font-semibold underline text-plum-950 underline-offset-[3px] hover:text-plum-800 transition-colors"
      >
        {children}
      </a>
    ),
    h1: () => {
      throw new Error(
          '`h1` (#) is reserved for the case study title. Use `h2` (##) and below instead.'
      );
    },
    h2: ({ children, id }: JSX.IntrinsicElements['h2']) => (
      <Section contentWidth='content-xxs' padding='p-none' className="mt-6 mb-2 [&:first-child>*]:mt-0" id={id}>
          <Text as="h2" color="primary" size="xl" weight="semibold">
            {children}
          </Text>
      </Section>
    ),
    h3: ({ children, id }: JSX.IntrinsicElements['h2']) => (
      <Section contentWidth='content-xxs' padding='p-none' className="mt-6 mb-2" id={id}>
          <Text as="h3" color="primary" size="lg" weight="semibold">
            {children}
          </Text>
      </Section>
    ),
    h4: ({ children, id }: JSX.IntrinsicElements['h2']) => (
      <Section contentWidth='content-xxs' padding='p-none' className="mt-6 mb-2" id={id}>
          <Text as="h4" color="primary" size="md" weight="semibold">
            {children}
          </Text>
      </Section>
    ),
    p: (props: JSX.IntrinsicElements['p']) => {
    // Avoid wrapping images in unnecessary paragraph tags.
      if (props.children && typeof props.children !== 'string') {
          if (
          isImageNode(props.children) ||
          (Array.isArray(props.children) &&
              isImageNode(props.children[0]))
          )
          return <>{props.children}</>;
      }
      return (
        <Section contentWidth='content-xxs' padding='p-none'>
          <Text as="p" className="leading-8 mt-2 mb-2 [&:first-child>*]:mt-0 whitespace-pre-line">
            {props.children}
          </Text>
        </Section>
      );
    },
    ul: ({ children }) => (
      <Section contentWidth='content-xxs' padding='p-none'>
        <ul className="list-disc pl-6 mt-2 mb-2 space-y-2">
          {children}
        </ul>
      </Section>
    ),
    ol: ({ children }) => (
      <Section contentWidth='content-xxs' padding='p-none'>
        <ol className="list-decimal pl-6 mt-2 mb-2 space-y-2">
          {children}
        </ol>
      </Section>
    ),
    li: ({ children }) => (
      <Text as="li" className="leading-6 before:inline">{children}</Text>
    ),
    em: ({ children }) => (
      <em className="italic">{children}</em>
    ),
    strong: ({ children }) => (
      <strong className="font-semibold">{children}</strong>
    ),
    code: ({ children }) => (
      <code className="bg-silvermist-100 text-silvermist-800 px-1.5 py-0.5 rounded text-sm font-mono">
        {children}
      </code>
    ),
    pre: ({ children }) => (
      <Section contentWidth='content-xxs' padding='p-none'>
        <pre className="bg-silvermist-100 text-silvermist-800 p-4 rounded-lg overflow-x-auto text-sm font-mono mt-4 mb-4">
          {children}
        </pre>
      </Section>
    ),
    hr: () => (
      <Section contentWidth='content-xxs' padding='p-none'>
        <hr className="border-t border-silvermist-200 my-8" />
      </Section>
    ),
    img: Img,
    blockquote: (props: JSX.IntrinsicElements['blockquote']) => {
      const { children, ...rest } = props;
      
      let content = children;

      if (children && typeof children === 'object' && 'props' in children) {
        // If it's a single paragraph, unwrap it
        if (children.type === 'p') {
          content = children.props.children;
        }
      }
    
      return <blockquote {...rest}>{content}</blockquote>;
    },
    table: Table,
    tr: TableRow,
    th: TableHeader,
    td: TableCell,
  };

  useEffect(() => {
    const compileMDX = async () => {
      const compiled = await serialize(caseStudyData.content);
      setMdxSource(compiled);
    };
    compileMDX();
  }, [caseStudyData.content]);

  return (
    <MarketingPage background="white">
      <Meta title={`${caseStudyData.title}`}>
        <meta property="og:title" content={caseStudyData.title} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`${LOGGED_IN_APP_BASE_URL}/blog/${caseStudyData.slug}`} />
        <meta
          property="og:image"
          content={`${LOGGED_IN_APP_BASE_URL}/api/og?img=${encodeURIComponent(
            caseStudyData.featuredImage
          )}`}
        />
        <meta property="og:description" content={caseStudyData.description} />
      </Meta>

      <div className='flex flex-col items-center text-silvermist-600'>
        <MDXRemote {...caseStudyData.content} components={components} />
      </div>

      <Section contentWidth='content-sm' padding='p-none' className='border border-silvermist-100 mb-0 lg:mb-10'>
        <CTABox roundedBorder={false} border={false} />
      </Section>

    </MarketingPage>
  );
};